import { CardNumberElement } from '@stripe/react-stripe-js'
import * as routes from 'config/routes'
import { useCallback, useEffect, useMemo, useState } from 'react'
// Config
import { TABOOLA_ID } from '../../../init'
import {
  getPricesUnregisteredUser,
  updateVATUnregisteredUser,
} from 'modules/taxes/actions'

const PAYMENT_PROVIDER = {
  PAYPAL: 'paypal',
}

const PAYMENT_TYPE = {
  SUBSCRIPTION: 'subscription',
  VIDEO: 'video',
}

const usePayment = ({
  actions,
  common,
  elements,
  history,
  match,
  stripe,
  taxes,
  type,
  validator,
  user,
  geodata: {
    countries: { payload: countries },
  },
}) => {
  // Setup
  const { searchParams } = common
  const { locale } = match.params
  const userData = user?.currentUser?.payload
  const vat = userData?.company?.vat_id
  const activeAgreement = user?.activeAgreements?.payload[0]

  // State
  const [isOpenOTPModal, setStateOTPModal] = useState(false)
  const [isStep, setStep] = useState(0)
  const [updBilling, setUpdBilling] = useState(0)
  const [isPendingPayment, setPendingPayment] = useState(false)
  const [currentClientSecret, setCurrentClientSecret] = useState()
  const [form, setForm] = useState({
    agreement_request: {},
    newsletter_notify: true,
    user: getInitialUser(),
  })

  function getInitialUser() {
    return user.isSignedIn
      ? userData
      : {
          country_id: '',
          first_name: '',
          last_name: '',
          profile_locale: '',
          password: '',
          password_confirmation: '',
          confirmation_token: '',
          not_a_company: true,
          company: {
            country_id: (countries[0] || { id: 3 }).id,
            has_no_vat: false,
            vat_id: '',
          },
          selected_payment_provider: 'stripe',
          have_pass: false,
        }
  }

  const showError = useCallback(
    msg => {
      actions.notifications.createNotification({
        type: 'error',
        children: msg,
      })
    },
    [actions]
  )

  // Data
  const agreementId = searchParams.agreement_id
  const isSubscriptionPayment = !!searchParams.agreement_id

  // Methods
  const fetchPrice = useCallback(
    type => {
      actions.taxes.getPrices(
        type === 'video'
          ? {
              type,
              branded:
                searchParams?.video_format?.brand_position === 'without_brand'
                  ? 'without_brand'
                  : 'with_brand',
              vod_external_name: searchParams?.project_slug,
            }
          : {
              type,
              plan: searchParams?.plan,
            }
      )
    },
    [actions, match, searchParams]
  )
  const fetchPriceUnregistered = useCallback(() => {
    const { type } = match?.params || {}
    const params = {
      type,
      branded:
        searchParams?.video_format?.brand_position === 'without_brand'
          ? 'without_brand'
          : 'with_brand',
      vod_external_name: searchParams?.project_slug,
    }

    const selectedCountry =
      countries.find(country => country.id === form.user.country_id) || {}

    if (selectedCountry) {
      params.country_code = selectedCountry.code
    }

    actions.taxes.getPricesUnregisteredUser(params)
  }, [actions, match, searchParams, countries, form.user.country_id])

  // Effects
  useEffect(() => {
    if (
      activeAgreement &&
      Number.isInteger(activeAgreement.id) &&
      activeAgreement.id !== agreementId &&
      type === PAYMENT_TYPE.VIDEO
    ) {
      actions.common.setSearchParams({
        ...searchParams,
        agreement_id: activeAgreement.id,
      })
    }
  }, [activeAgreement, agreementId, searchParams, type])

  useEffect(() => {
    const { plan } = searchParams

    if (user.isSignedIn && searchParams.plan) {
      actions.user.getPlan(searchParams.plan)
      actions.user.getPurchasePossibility('pass')

      if (Number.isInteger(agreementId)) {
        actions.user.getAgreements().then(res => {
          const { body } = res
          const plan = (body || []).find(
            agreement => agreement.id === agreementId
          )

          if (plan) {
            setForm({
              ...form,
              agreement_request: { url_1: plan.url_1, url_2: plan.url_2 },
            })
          }

          return res
        })
      }

      ;['pass-annually-eur', 'pass-monthly-eur'].indexOf(plan) > -1 &&
        window._tfa &&
        window._tfa.push({
          notify: 'event',
          name: 'start_checkout',
          id: TABOOLA_ID,
        })
    } else {
      searchParams.project_id &&
        actions.user.getActiveAgreements(
          {
            content_id: searchParams.project_id,
            content_type: 'vod',
          },
          user.isSignedIn
        )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isSignedIn, agreementId, searchParams])

  useEffect(() => {
    const { type } = match?.params || {}
    if (user.isSignedIn) {
      fetchPrice(type)
      setStep(2)
    } else if (type === 'video') {
      fetchPriceUnregistered()
    }
  }, [vat, user.isSignedIn])

  useEffect(() => {
    if (
      !user.isSignedIn &&
      !taxes.VATValidate.pending &&
      Number.isInteger(form.user.country_id)
    ) {
      const { plan } = searchParams

      if (plan) {
        actions.taxes.updateVATUnregisteredUser({
          plan,
          country_id: form.user.country_id,
        })
      } else {
        fetchPriceUnregistered()
      }
    }
  }, [
    taxes.VATValidate.payload.valid,
    user.isSignedIn,
    form.user.country_id,
    searchParams.plan,
    taxes.VATValidate.pending,
    actions.taxes,
  ])

  useEffect(() => {
    if (user.isSignedIn) {
      const { vat_id } = form.user.company || {}
      const { country_id } = userData.company
        ? userData.company
        : form.user.company
      actions.taxes.validate(vat_id || '', country_id)
    }
  }, [
    form.user.company?.vat_id,
    form.user.company?.country_id,
    user.isSignedIn,
    userData.company?.vat_id,
    userData.company?.country_id,
  ])

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      user: getInitialUser(),
    }))
  }, [user.isSignedIn])

  // Handlers
  const nextStepHandler = useCallback(() => {
    setStep(prevState => prevState + 1)
  }, [])

  const handleFormChange = useCallback(
    (event, newForm) => {
      const updForm = {
        ...form,
        ...newForm,
        user: { ...form.user, ...newForm.user },
      }
      const currentCountry =
        countries.find(({ id }) => id === +updForm.user.country_id) || {}

      if (
        event.target &&
        [PAYMENT_TYPE.VIDEO, PAYMENT_TYPE.SUBSCRIPTION].includes(type) &&
        Number.isInteger(agreementId)
      ) {
        setUpdBilling(prev => prev || event.target.name.match(/^user\[/))
      }
      if (currentCountry.code === 'RU') {
        updForm.user.not_a_company = false
      }

      setForm(updForm)

      if (event.target.name === 'user[company][has_no_vat]') {
        if (user.isSignedIn && event.target.checked) {
          actions.taxes.updateVAT('', true)
        }
      }

      setPendingPayment(false)
    },
    [actions, form, user.isSignedIn, countries]
  )

  const handleSubscriptionPayment = useCallback(async () => {
    const { agreement_id } = searchParams

    if (form.user.selected_payment_provider === PAYMENT_PROVIDER.PAYPAL) {
      const { redirect_url } = (
        await actions.user.initiatePayPalPassPayment({
          agreement_id,
          redirect_url: `${
            window.location.origin
          }${routes.paymentProcessing.getPath(locale)}?plan=${
            searchParams.plan
          }&agreement_id=${searchParams.agreement_id}`,
        })
      ).body

      window.location.href = redirect_url
    } else {
      const { external_id = null } =
        user.currentUser.payload.payment_method || {}

      let paymentMethodId = external_id

      if (!paymentMethodId) {
        const res = await stripe.createPaymentMethod({
          type: 'card',
          card: elements.getElement(CardNumberElement),
        })

        if (res.error) {
          throw new Error(res.error.message)
        }

        paymentMethodId = res.paymentMethod.id
      }

      if (paymentMethodId) {
        const runPayment = async () => {
          const { payment_intent } = (
            await actions.user.subscribePassStripePayment({
              agreement_id: agreementId,
              payment_method: paymentMethodId,
            })
          ).body

          switch (payment_intent.status) {
            case 'requires_action':
            case 'requires_payment_method':
              const { client_secret } = payment_intent

              const { error } = await stripe.confirmCardPayment(client_secret, {
                payment_method: paymentMethodId,
              })

              if (error) {
                throw error
              }

              await runPayment()
              break
            // case 'requires_payment_method':
            //   throw new Error('The card has been rejected!');
            case 'succeeded':
              history.push(
                `${routes.paymentProcessing.getPath(
                  locale
                )}?agreement_id=${agreementId}&payment_intent_id=${
                  payment_intent.id
                }`
              )
              break
            default:
              break
          }
        }

        await runPayment()
        setPendingPayment(false)
      } else {
        setPendingPayment(false)
        throw new Error('Payment method missing!')
      }
    }
  }, [
    actions,
    agreementId,
    elements,
    form,
    history,
    locale,
    searchParams,
    stripe,
    user,
  ])

  const handleSubscriptionRequest = useCallback(async () => {
    const { agreement_request } = form
    const plan = user.plan.payload

    if (user.plan.payload) {
      await actions.user.agreementRequestForUser({
        agreement_request: {
          agreement_type: 'pass',
          payment_plan_id: plan.id,
          url_1: agreement_request.url_1,
          url_2: agreement_request.url_2,
        },
      })

      history.push(routes.planDetails.getPath(locale))
      setPendingPayment(false)
    }
  }, [actions, form, history, locale, user])

  const handleVideoPayment = useCallback(
    async (event, values) => {
      const { company, ...formUser } = form.user

      const videoParams = {
        brand_position: searchParams?.video_format?.brand_position,
        extension: searchParams?.video_format?.extension,
        file_type: searchParams?.video_format?.file_type,
        quality: searchParams?.video_format?.quality,
        video_project_id: searchParams?.project_id,
        project_slug: searchParams?.project_slug,
      }

      if (formUser.selected_payment_provider === PAYMENT_PROVIDER.PAYPAL) {
        const { redirect_url } = (
          await actions.user.initiatePayPalVideoPayment({
            ...videoParams,
            redirect_url: `${
              window.location.origin
            }${routes.paymentProcessing.getPath(locale)}?project_slug=${
              videoParams.project_slug
            }&brand_position=${videoParams.brand_position}&agreement_type=${
              activeAgreement?.agreement_type
            }&project_id=${videoParams.video_project_id}&download_to_sftp=${
              searchParams.download_to_sftp
            }&quality=${videoParams.quality}&extension=${
              videoParams.extension
            }&file_type=${videoParams.file_type}`,
          })
        ).body

        window.location.replace(redirect_url)
      } else {
        const { external_id = null } =
          user.currentUser.payload.payment_method || {}

        let clientSecret = currentClientSecret

        if (!currentClientSecret) {
          const { client_secret } = (
            await actions.user.initiateStripeVideoPayment({
              ...videoParams,
              payment_method: form.card_holder ? null : external_id,
            })
          ).body

          setCurrentClientSecret(client_secret)
          clientSecret = client_secret
        }

        const { error, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          form.card_holder && {
            payment_method: { card: elements.getElement(CardNumberElement) },
          }
        )

        if (error) {
          setPendingPayment(false)
          throw error
        }

        history.push(
          `${routes.paymentProcessing.getPath(locale)}?payment_intent_id=${
            paymentIntent.id
          }&project_slug=${videoParams.project_slug}&brand_position=${
            videoParams.brand_position
          }&agreement_type=${activeAgreement?.agreement_type}`
        )
        setPendingPayment(false)
      }
    },
    [
      form.user,
      form.card_holder,
      searchParams,
      actions.user,
      locale,
      activeAgreement?.agreement_type,
      user.currentUser.payload.payment_method,
      currentClientSecret,
      stripe,
      elements,
      history,
    ]
  )

  const handleSuccessConfirmOTP = useCallback(confirmation_token => {
    setForm(prevForm => ({
      ...prevForm,
      user: {
        ...prevForm.user,
        confirmation_token,
      },
    }))
    setStateOTPModal(false)
    nextStepHandler()
  }, [])

  const handleFormSubmit = useCallback(
    async (event, values) => {
      const { company, ...formUser } = form.user

      if (validator.panic()) {
        setPendingPayment(true)

        if (updBilling) {
          actions.user.updateBillingInfo({
            ...formUser,
            company_attributes: company,
          })
        }

        if (isStep === 0) {
          try {
            await actions.user.getPassByUnregisteredCheckEmail(values.email)
            setPendingPayment(false)
            actions.common.openSignInModal(values.email)
          } catch (e) {
            actions.user
              .registrationOfOTP(values.email)
              .then(res => {
                if (res.status === 200) {
                  setStateOTPModal(true)
                }
              })
              .finally(() => {
                setPendingPayment(false)
              })
          }
        } else if (isStep === 1) {
          actions.user
            .confirmByToken({
              user: {
                company_name: form.user.company_name,
                confirmation_token: form.user.confirmation_token,
                country_id: form.user.country_id,
                first_name: form.user.first_name,
                last_name: form.user.last_name,
                password: form.user.password,
                password_confirmation: form.user.password_confirmation,
                profile_locale: locale,
              },
            })
            .then(() => {
              actions.user
                .signIn({
                  email: form.email,
                  password: form.user.password,
                })
                .then(() => {
                  const { plan } = common.searchParams
                  plan && actions.user.getPlan(plan).catch(() => {})
                  actions.user.checkPlanningAccess().catch(() => {})
                })
              window._tfa &&
                window._tfa.push({
                  notify: 'event',
                  name: 'complete_registration',
                  id: TABOOLA_ID,
                })
              nextStepHandler()
            })
            .finally(() => {
              setPendingPayment(false)
            })
        } else {
          try {
            type === PAYMENT_TYPE.VIDEO
              ? await handleVideoPayment(event, values)
              : isSubscriptionPayment
              ? await handleSubscriptionPayment(event, values)
              : await handleSubscriptionRequest(event, values)
          } catch (error) {
            setPendingPayment(false)

            if (error?.response?.body?.errors) {
              error.response.body.errors.forEach(({ code, message }) => {
                showError(message)
              })
            } else {
              showError(error.message)
            }

            throw error
            // }
          }
        }
      }
    },
    [
      locale,
      history,
      showError,
      searchParams,
      updBilling,
      user,
      actions,
      form,
      handleSubscriptionPayment,
      handleSubscriptionRequest,
      handleVideoPayment,
      isSubscriptionPayment,
      type,
      validator,
      isStep,
    ]
  )

  // Validatiors
  const VATValidator = useMemo(
    () => ({
      func: () => taxes.VATValidate.payload.valid,
      message: 'VAT ID is invalid',
      pending: taxes.VATValidate.pending,
    }),
    [taxes.VATValidate.payload.valid, taxes.VATValidate.pending]
  )

  const handleVATChange = useCallback(
    ({ target: { value } }) => {
      const { country_id } = userData.company
        ? userData.company
        : form.user.company
      actions.taxes.validate(value, country_id)
    },
    [actions.taxes, form.user.company, userData.company]
  )

  return {
    form,
    handleFormChange,
    handleFormSubmit,
    VATValidator,
    handleVATChange,
    isPendingPayment,
    nextStepHandler,
    isStep,
    isOpenOTPModal,
    handleSuccessConfirmOTP,
  }
}

export { PAYMENT_PROVIDER, PAYMENT_TYPE, usePayment }
